import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/Button";
import { useProgrammesList } from "../../../../hooks/useProgrammesList";
import { useDeleteProgramme } from "../../../../hooks/useDeleteProgramme";
import type { ParcoursResponse } from "../../../../api/parcoursApi";
import type { ProgrammeResponse } from "../../../../api/programmesApi";

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function formatDayLabel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS_FR[m - 1]} ${y}`;
}

function fmtTime(t?: string | null): string {
  return t ? t.slice(0, 5) : "";
}

function fmtShortDate(iso: string): string {
  const [, m, d] = iso.split("-");
  return m && d ? `${d}/${m}` : iso;
}

export default function ParcoursProgrammesTab() {
  const { id = "" } = useParams<{ id: string }>();
  const parcours = useOutletContext<ParcoursResponse>();
  const navigate = useNavigate();
  const { data, isLoading } = useProgrammesList({ parcoursId: id, page: 0, size: 200, sort: "date,asc" });
  const remove = useDeleteProgramme();

  const programmes = data?.content ?? parcours.programmes ?? [];

  if (parcours.contentMode === "DESCRIPTION") {
    return (
      <div className="bo-card" style={{ textAlign: "center" }}>
        <p style={{ color: "var(--ink-mute)", fontSize: 14, margin: 0 }}>
          Ce parcours affiche une <strong>description longue</strong> à la place du programme.
          Repasser en mode « Programme » dans l'onglet « Infos générales » pour ajouter des programmes.
        </p>
      </div>
    );
  }

  // Group programmes by date string
  const byDate = new Map<string, ProgrammeResponse[]>();
  programmes.forEach((p) => {
    const key = p.date ?? "__no-date__";
    const list = byDate.get(key) ?? [];
    list.push(p);
    byDate.set(key, list);
  });

  // Each slot covers [date, endDate ?? date] inclusive; a programme belongs to it
  // when its date falls inside the range. Programmes without any covering slot
  // are surfaced separately.
  const slots = parcours.dates ?? [];
  const covered = new Set<string>();
  const slotProgrammes = slots.map((slot) => {
    const start = slot.date;
    const end = slot.endDate || slot.date;
    const list: ProgrammeResponse[] = [];
    byDate.forEach((progs, date) => {
      if (date === "__no-date__") return;
      if (date >= start && date <= end) {
        covered.add(date);
        list.push(...progs);
      }
    });
    return list;
  });
  const extraDates = Array.from(byDate.keys())
    .filter((d) => d !== "__no-date__" && !covered.has(d))
    .sort();

  const goCreate = (date?: string) =>
    navigate(`/admin/parcours/${id}/programmes/new${date ? `?date=${date}` : ""}`);

  if (slots.length === 0 && programmes.length === 0) {
    return (
      <div className="bo-card" style={{ textAlign: "center" }}>
        <p style={{ color: "var(--ink-mute)", fontSize: 14, margin: 0 }}>
          Ajoutez d'abord des créneaux au parcours (onglet « Infos générales ») pour pouvoir y attacher des programmes.
        </p>
      </div>
    );
  }

  return (
    <div className="bo-stack">
      {isLoading && <div className="bo-loading">Chargement…</div>}

      {slots.map((slot, i) => {
        const multiDay = slot.endDate && slot.endDate !== slot.date;
        const label = multiDay
          ? `Du ${formatDayLabel(slot.date)} au ${formatDayLabel(slot.endDate as string)}`
          : formatDayLabel(slot.date);
        const hours = [fmtTime(slot.startTime), fmtTime(slot.endTime)].filter(Boolean).join(" – ");
        return (
          <DaySection
            key={slot.id}
            dayLabel={label}
            hours={hours}
            programmes={slotProgrammes[i]}
            onAdd={() => goCreate(slot.date)}
            onEdit={(p) => navigate(`/admin/parcours/${id}/programmes/${p.id}`)}
            onDelete={(p) => window.confirm("Supprimer ce programme ?") && remove.mutate(p.id)}
          />
        );
      })}

      {extraDates.map((date) => (
        <DaySection
          key={date}
          dayLabel={formatDayLabel(date)}
          hours=""
          subtitle="Date hors des créneaux déclarés du parcours"
          programmes={byDate.get(date) ?? []}
          onAdd={() => goCreate(date)}
          onEdit={(p) => navigate(`/admin/parcours/${id}/programmes/${p.id}`)}
          onDelete={(p) => window.confirm("Supprimer ce programme ?") && remove.mutate(p.id)}
        />
      ))}

      {byDate.has("__no-date__") && (
        <DaySection
          dayLabel="Sans date"
          hours=""
          subtitle="Programmes sans date — éditez-les pour les rattacher à un jour"
          programmes={byDate.get("__no-date__") ?? []}
          onAdd={undefined}
          onEdit={(p) => navigate(`/admin/parcours/${id}/programmes/${p.id}`)}
          onDelete={(p) => window.confirm("Supprimer ce programme ?") && remove.mutate(p.id)}
        />
      )}
    </div>
  );
}

interface DaySectionProps {
  dayLabel: string;
  hours?: string;
  subtitle?: string;
  programmes: ProgrammeResponse[];
  onAdd?: () => void;
  onEdit: (p: ProgrammeResponse) => void;
  onDelete: (p: ProgrammeResponse) => void;
}

function DaySection({ dayLabel, hours, subtitle, programmes, onAdd, onEdit, onDelete }: DaySectionProps) {
  return (
    <section className="bo-card">
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "var(--sp-4)",
          gap: "var(--sp-3)",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 20, textTransform: "uppercase" }}>
            {dayLabel}
          </h3>
          {hours && (
            <div style={{ fontSize: 12, color: "var(--ink-mute)", marginTop: 4 }}>
              Ouverture : {hours}
            </div>
          )}
          {subtitle && (
            <div style={{ fontSize: 12, color: "var(--terracotta-d)", marginTop: 4 }}>{subtitle}</div>
          )}
        </div>
        {onAdd && (
          <Button type="button" variant="primary" onClick={onAdd}>
            + Programme
          </Button>
        )}
      </header>

      {programmes.length === 0 ? (
        <p style={{ color: "var(--ink-mute)", fontSize: 13, margin: 0 }}>
          Aucun programme pour ce jour.
        </p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
          {programmes
            .slice()
            .sort((a, b) => (a.time ?? "").localeCompare(b.time ?? ""))
            .map((p) => {
              const start = fmtTime(p.time);
              const end = fmtTime(p.endTime);
              const multiDay = p.endDate && p.endDate !== p.date;
              // Compact time label: "10:30–12:00", "10:30", "→ 12:00", or "—".
              let whenLabel: string;
              if (multiDay) {
                whenLabel = start && end ? `${start} → ${end}` : start || end || "—";
              } else if (start && end) {
                whenLabel = `${start}–${end}`;
              } else {
                whenLabel = start || (end ? `→ ${end}` : "—");
              }
              return (
              <li
                key={p.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "80px 1fr auto",
                  gap: "var(--sp-3)",
                  alignItems: "center",
                  padding: "var(--sp-3)",
                  background: "var(--paper)",
                  border: "1px solid var(--line-soft)",
                  borderRadius: "var(--radius-s)",
                }}
              >
                <div style={{ fontFamily: "var(--font-display)", fontSize: 16, color: "var(--ink)" }}>
                  {fmtTime(p.time) || "—"}
                  {(p.endTime || (p.endDate && p.endDate !== p.date)) && (
                    <div style={{ fontSize: 11, color: "var(--ink-mute)", fontFamily: "var(--font-body)" }}>
                      → {[p.endDate && p.endDate !== p.date ? fmtShortDate(p.endDate) : null, fmtTime(p.endTime) || null]
                        .filter(Boolean)
                        .join(" ")}
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: "var(--ink)" }}>{p.title}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-mute)" }}>
                    {[p.subtitle, p.lieu?.name ?? p.addressName].filter(Boolean).join(" · ") || "—"}
                    {multiDay && <> · jusqu'au {p.endDate}</>}
                  </div>
                </div>
                <div className="bo-row-end">
                  <Button variant="ghost" onClick={() => onEdit(p)}>
                    Éditer
                  </Button>
                  <Button variant="ghost" onClick={() => onDelete(p)}>
                    Supprimer
                  </Button>
                </div>
              </li>
              );
            })}
        </ul>
      )}
    </section>
  );
}
